import {
	EventBusOptions,
	EventBusParams,
	Listener,
	ReplayType,
	SystemCommand,
	TypedFunctionCallback,
} from "./event-bus.types";

import {
	DynamicValueDescriptor,
	EventBusCommand,
	ExposeObserver,
	ExposeOptions,
	GenericObserver,
	NotifierProperties,
	ObservableCreator,
	CacheMeta,
	Subscriber,
	CommandConfiguration,
	ObservableWithType,
	InterceptorData,
	GenericSubscription,
} from "./types";

/**
 * Simple Event Bus implementation - Singleton + Publisher/Subscriber pattern
 * public method for dispatching event and listening to it
 * options for replaying events: none, latest, all
 *
 * Buffering:
 * Event bus fills event buffer as handlers are subscribing asynchronously - it is up to handler to decide
 * if dispatched events are needed and upon subscribing to listen can replay them.
 * As soon as Event handler starts listening and potentially replays already dispatched events,
 * buffer for particular command is set to 'nobuffer' and no more events are buffered.
 * In case there is no listener subscription after  for particular commands - we set 'nolistener' and print warning;
 */
export class EventBus<Contracts, ValuesContracts = Record<string, unknown>> {
	private latestId = 0;

	// Buffering events for replay by late loaded event handlers
	private eventBuffer: Record<string, unknown[]> = {};

	// List of commands to check for listeners and garbage collector

	private bufferLimit = { maxEvents: 100 }; // How many dispatches are kept in buffer before garbage collected
	private toGarbageCollect: Record<string, number> = {};

	// All subscribers categorized by command name - each holding id for later unsubscribe
	// In case optimization is needed setTimeout can be used to take care of each event subscribers
	private listeners: Record<string, Listener<TypedFunctionCallback<unknown>>[]> = {};

	private subscribers: Subscriber[] = [];
	private observableLatestMeta: Record<string, CacheMeta<unknown>> = {};
	private commandConfigurations: Record<string, CommandConfiguration> = {};
	/**  a record of command as key and observer as value */
	private notifierObservers: Record<string, GenericObserver<NotifierProperties<unknown, unknown>>> = {};
	/** a record of stringified command+initialProps as key and observer as value */
	private interceptorSubscriptions: Record<string, GenericSubscription> = {};
	private interceptorData: Record<string, InterceptorData> = {};
	private isDebugEnabled: boolean;

	constructor(private params?: EventBusParams) {
		this.isDebugEnabled = this.params?.isDebugEnabled || false;
	}

	expose<
		Command extends keyof ValuesContracts & string,
		Value = ValuesContracts[Command],
		ValueType = Value extends DynamicValueDescriptor<unknown, unknown, unknown> ? Value["_value$"] : Value,
		ReturnType = Value extends DynamicValueDescriptor<unknown, unknown, unknown> ? void : ExposeObserver<ValueType>,
		InputProps = Value extends DynamicValueDescriptor<void, unknown, unknown>
			? void
			: Value extends DynamicValueDescriptor<unknown, unknown, unknown>
				? Value["_props$"]
				: void,
	>(
		command: Command,
		options?: ExposeOptions<
			InputProps,
			ValueType,
			Value extends DynamicValueDescriptor<unknown, unknown, unknown> ? Value["_modifiedProps$"] : void
		>
	): ReturnType {
		if (command in this.notifierObservers || command in this.commandConfigurations) {
			console.error(
				`Command "${command}" is already exposed, it is not allowed to expose the same command twice.`,
				`If you are receiving this error, it is most certain that you have a serious bug in your code.`
			);
			return {
				next: (value: ValuesContracts[Command]) =>
					this.nextFn({
						value,
						command,
						cacheKey: this.getCacheKey(command, undefined),
						initialCacheKey: this.getCacheKey(command, undefined),
					}),
			} as ReturnType;
		}

		const disableLastValueCaching = typeof options === "function" ? false : !!options?.disableLastValueCaching;
		const disableVerboseNotifiers = typeof options === "function" ? false : !!options?.disableVerboseNotifiers;
		const notifierToUse = typeof options === "function" ? options : options?.notifier;
		const cacheKeyFn =
			typeof options === "function" ? undefined : (options?.cacheKey as (props: unknown) => string | string[]);
		const interceptor =
			typeof options === "function"
				? undefined
				: (options?.interceptor as (props: unknown) => ObservableWithType<unknown>);

		this.commandConfigurations[command] = {
			disableLastValueCaching,
			disableVerboseNotifiers,
			interceptor,
			cacheKeyFn,
		};

		notifierToUse?.((observer) => {
			this.notifierObservers[command] = observer as GenericObserver<NotifierProperties<unknown, unknown>>;

			return () => {
				delete this.notifierObservers[command];
			};
		}).subscribe({
			next: () => {},
			error: (error) => {
				console.error(`UNHANDLED notifier in ${command}: Error in notifier`, error);
			},
		});

		for (const subscriber of this.subscribers.filter((x) => x.command === command)) {
			this.addPropSubscription(subscriber.id);
		}

		return {
			next: (value: ValuesContracts[Command]) =>
				this.nextFn({
					value,
					command,
					cacheKey: this.getCacheKey(command, undefined),
					initialCacheKey: this.getCacheKey(command, undefined),
				}),
		} as ReturnType;
	}

	retrieve<
		Command extends keyof ValuesContracts & string,
		Value = ValuesContracts[Command],
		Props = Value extends DynamicValueDescriptor<unknown, unknown, unknown> ? Value["_props$"] : void,
		HasProps = Props extends void
			? false
			: Value extends DynamicValueDescriptor<void, unknown, unknown>
				? false
				: true,
	>(
		command: Command,
		...args: HasProps extends true ? [Props] : []
	): ObservableCreator<
		Value extends DynamicValueDescriptor<unknown, unknown, unknown> ? Value["_value$"] : ValuesContracts[Command]
	> {
		const commandToUse = command;
		const props = typeof args[0] === "object" && args[0] !== null && "props" in args[0] ? args[0].props : args[0];

		return ((subscriber: GenericObserver<unknown>) => {
			const id = Math.random().toString(36).substring(2) + Date.now().toString(36);
			this.subscribers.push({
				id,
				subscriber,
				initialProps: props,
				command: commandToUse,
				initialCacheKey: this.getCacheKey(commandToUse, props),
				hasInitialValue: false,
			});

			this.addPropSubscription(id);

			return () => {
				const subscriber = this.subscribers.find(({ id: subscribeId }) => subscribeId === id);
				if (!subscriber) {
					return;
				}

				this.subscribers = this.subscribers.filter(({ id: subscribeId }) => subscribeId !== id);
				const data = this.interceptorData[subscriber.initialCacheKey];

				this.triggerNotifier({
					command: subscriber.command,
					cacheKey: data ? data.cacheKey : subscriber.initialCacheKey,
					props: data ? data.computedProps : subscriber.initialProps,
					lastAction: "unsubscribe",
					initialCacheKey: subscriber.initialCacheKey,
					isSubscriberChange: true,
				});

				const similarSubscribers = this.subscribers.filter(
					({ initialCacheKey }) => initialCacheKey === subscriber.initialCacheKey
				);

				if (!similarSubscribers.length) {
					delete this.interceptorData[subscriber.initialCacheKey];

					this.interceptorSubscriptions[subscriber.initialCacheKey]?.unsubscribe();
					delete this.interceptorSubscriptions[subscriber.initialCacheKey];
				}
			};
		}) as ObservableCreator<
			Value extends DynamicValueDescriptor<unknown, unknown, unknown>
				? Value["_value$"]
				: ValuesContracts[Command]
		>;
	}

	private addPropSubscription(id: string) {
		const subscriber = this.subscribers.find(({ id: subscribeId }) => subscribeId === id);
		if (!subscriber) {
			return;
		}
		const configuration = this.commandConfigurations[subscriber.command];
		if (!configuration) {
			return;
		}

		if (!configuration.interceptor) {
			const cacheKey = this.getModifiedCacheKey(subscriber.command, subscriber.initialProps);
			const meta = this.getCacheMeta(cacheKey);
			if (meta.hasValue && !subscriber.hasInitialValue) {
				subscriber.subscriber.next(meta.value);
			}

			this.triggerNotifier({
				command: subscriber.command,
				cacheKey: cacheKey,
				props: subscriber.initialProps,
				lastAction: "subscribe",
				isSubscriberChange: true,
				initialCacheKey: subscriber.initialCacheKey,
			});
			return;
		}

		const data = this.interceptorData[subscriber.initialCacheKey];
		if (data) {
			const meta = this.getCacheMeta(data.cacheKey);

			if (meta.hasValue && !subscriber.hasInitialValue) {
				subscriber.subscriber.next(meta.value);
			}

			this.triggerNotifier({
				command: subscriber.command,
				cacheKey: data.cacheKey,
				props: data.computedProps,
				lastAction: "subscribe",
				isSubscriberChange: true,
				initialCacheKey: subscriber.initialCacheKey,
			});
			return;
		}

		if (this.interceptorSubscriptions[subscriber.initialCacheKey]) {
			return;
		}

		this.interceptorSubscriptions[subscriber.initialCacheKey] = configuration
			.interceptor(subscriber.initialProps)
			.subscribe((props) => {
				const cacheKey = this.getModifiedCacheKey(subscriber.command, props);
				this.registerInterceptorData(subscriber.initialCacheKey, cacheKey, props);
				const meta = this.getCacheMeta(cacheKey);

				for (const { subscriber: sub } of this.subscribers.filter(
					({ initialCacheKey }) => initialCacheKey === subscriber.initialCacheKey
				)) {
					if (meta.hasValue) {
						sub.next(meta.value);
					}
				}

				this.triggerNotifier({
					command: subscriber.command,
					cacheKey,
					props,
					lastAction: "subscribe",
					isSubscriberChange: false,
					initialCacheKey: subscriber.initialCacheKey,
				});
			});
	}

	/**
	 * Dispatches event
	 * @param command string unique name
	 * @param payload data based on contract for particular command
	 */
	dispatch<Command extends keyof Contracts & string>(
		command: Command,
		payload?: Contracts[Command]
		/**
		 * This will be enabled when all the other fixes that was included in #AC-8167
		 * was merged and deployed (both mFE and OBGA)
		 * Then we can remove the above line and re-add the below line
		 * so we'll have optional payload only when we want it to be optional.
		 */
		// ...args: [Contracts[Command]] extends [undefined | never] ? [] : [payload: Contracts[Command]]
	) {
		// const [payload] = args;
		this.log("dispatch", command, payload);
		this.notifySubscribers(
			EventBusCommand.AllCommands as Command,
			{
				command,
				payload,
			} as Contracts[Command]
		);

		if (this.listeners[command]) {
			this.notifySubscribers(command, payload);
			return;
		}

		const eventBuffer = (this.eventBuffer[command] = this.eventBuffer[command] || []);
		if (eventBuffer[0] !== SystemCommand.nobuffer && eventBuffer[0] !== SystemCommand.nolistener) {
			this.eventBuffer[command] = [...(this.eventBuffer[command] || []), payload];
			// Setting garbage collector for future check
			this.toGarbageCollect[command] = (this.toGarbageCollect[command] || 0) + 1;
			if ((this.toGarbageCollect[command] || 0) > this.bufferLimit.maxEvents) {
				this.bufferGarbageCollection(command);
			}
		}
	}

	/**
	 *
	 * @param command string unique name
	 * @param callback
	 * @param eventBusOptions
	 */
	listen<Command extends keyof Contracts & string>(
		command: Command,
		callback: TypedFunctionCallback<Contracts[Command]>,
		eventBusOptions?: EventBusOptions
	) {
		const id = this.generateId();
		if (!this.listeners[command]) {
			this.listeners[command] = [];
		}

		this.listeners[command] = [
			...(this.listeners[command] || []),
			{
				id,
				callback: callback as TypedFunctionCallback<unknown>,
			},
		];

		// Handle replay
		const replay = eventBusOptions?.replay === undefined ? ReplayType.none : eventBusOptions?.replay;
		if (replay !== ReplayType.none) {
			const events = this.eventBuffer[command];
			if (events) {
				if (replay === ReplayType.latest) {
					// Replay last event
					const lastEvent = events[events.length - 1];
					if (lastEvent !== SystemCommand.nobuffer && lastEvent !== SystemCommand.nolistener) {
						callback(lastEvent as Contracts[Command]);
					}
				} else if (replay === ReplayType.all) {
					// Replay All Events
					for (const ev of events) {
						if (ev !== SystemCommand.nobuffer && ev !== SystemCommand.nolistener) {
							callback(ev as Contracts[Command]);
						}
					}
				}
			}
		}
		// Remove garbage collection and set command to not get buffered as there is registered listener
		this.eventBuffer[command] = [SystemCommand.nobuffer];
		delete this.toGarbageCollect[command];
		return () => this.unlisten(id, command);
	}

	destroy() {
		this.log("destroy() method not implemented yet");
	}

	private registerInterceptorData(initialCacheKey: string, cacheKey: string, props: unknown) {
		this.interceptorData[initialCacheKey] = {
			computedProps: props,
			cacheKey,
		};
	}

	private triggerNotifier({
		command,
		cacheKey,
		initialCacheKey,
		props,
		lastAction,
		isSubscriberChange,
	}: {
		command: string;
		cacheKey: string;
		initialCacheKey: string;
		props: unknown;
		lastAction: "subscribe" | "unsubscribe";
		isSubscriberChange: boolean;
	}) {
		if (!(command in this.notifierObservers)) {
			return;
		}

		const meta = this.getCacheMeta(cacheKey);

		const { value: _value, ...cacheMeta } = meta;
		const listeners = this.subscribers.filter(
			({ initialCacheKey: subscriberInitialCacheKey }) => subscriberInitialCacheKey === initialCacheKey
		).length;

		this.notifierObservers[command]?.next({
			props,
			lastAction,
			listeners,
			cacheKey,
			cacheMeta,
			pulseType: this.getPulseType(meta.notified, isSubscriberChange),
			setPending: (isPending: boolean) => this.setCacheMeta(cacheKey, { isPending }),
			next: (value) => this.nextFn({ value, command, cacheKey, initialCacheKey }),
		});

		this.setCacheMeta(cacheKey, { notified: true });
	}

	private getModifiedCacheKey(command: string, props: unknown) {
		const configuration = this.commandConfigurations[command];
		if (!configuration) {
			throw new Error(`Command configuration missing for "${command}".`);
		}

		const modifiedCacheKey = configuration.cacheKeyFn?.(props);
		return this.getCacheKey(command, modifiedCacheKey || props, undefined);
	}

	private getPulseType(isPulse: boolean, isSubscriberChange: boolean) {
		if (!isPulse) {
			return "initial";
		}

		return isSubscriberChange ? "subscriberChange" : "interceptorChange";
	}

	private getCacheMeta(cacheKey: string): CacheMeta<unknown> {
		const meta = this.observableLatestMeta[cacheKey];

		if (!meta) {
			this.setCacheMeta(cacheKey, {});
			return this.getCacheMeta(cacheKey);
		}

		return meta;
	}

	private setCacheMeta(cacheKey: string, data: Partial<CacheMeta<unknown>>): void {
		const existingMeta = this.observableLatestMeta[cacheKey];
		this.observableLatestMeta[cacheKey] = existingMeta
			? {
					...existingMeta,
					...data,
				}
			: {
					createdAt: new Date(),
					updatedAt: undefined,
					hasValue: false,
					isPending: false,
					notified: false,
					...data,
				};
	}

	private nextFn({
		value,
		command,
		cacheKey,
		initialCacheKey,
	}: {
		value: ((value: unknown) => unknown) | unknown;
		command: string;
		cacheKey: string;
		initialCacheKey: string;
	}) {
		const meta = this.getCacheMeta(cacheKey);
		const valueToSet = typeof value === "function" ? value(meta.value) : value;

		if (!this.commandConfigurations[command]?.disableLastValueCaching) {
			this.setCacheMeta(cacheKey, { value: valueToSet });

			this.setCacheMeta(cacheKey, {
				updatedAt: new Date(),
				hasValue: true,
			});
		}

		for (const subscriber of this.subscribers.filter(
			({ initialCacheKey: subInitialCacheKey }) => subInitialCacheKey === initialCacheKey
		)) {
			subscriber.subscriber.next(valueToSet);
			subscriber.hasInitialValue = true;
		}
	}

	private getCacheKey(command: string, props: unknown, uniqueId = "cache"): string {
		if (!props) {
			return `${command}:::${uniqueId}`;
		}
		return `${command}:::${uniqueId}:::${this.getStringifiedProps(props)}`;
	}

	private getStringifiedProps(obj: any): string {
		if (typeof obj === "function" || typeof obj === "symbol") {
			console.error("Props contain non-stringifiable values (like functions)", obj);
			return "error:::non-stringifiable-value";
		}

		if (typeof obj === "string" || typeof obj === "number" || typeof obj === "boolean" || typeof obj === "bigint") {
			return obj.toString();
		}

		if (typeof obj !== "object" || obj === null || obj instanceof Date) {
			return JSON.stringify(obj);
		}

		if (Array.isArray(obj)) {
			// Sort each element of the array recursively
			return JSON.stringify(obj.map((item) => this.getStringifiedProps(item)));
		}

		// Sort object keys recursively
		const sortedObj = Object.keys(obj)
			.sort()
			.reduce(
				(acc, key) => {
					acc[key] = this.getStringifiedProps(obj[key]);
					return acc;
				},
				{} as Record<string, any>
			);

		return JSON.stringify(sortedObj);
	}

	/**
	 * Removes listener from subscribers
	 * @param id generated id
	 * @param command event category
	 */
	private unlisten<Command extends keyof Contracts & string>(id: string, command: Command) {
		const newCommandListeners: Listener<TypedFunctionCallback<unknown>>[] = [];
		this.log(this.listeners[command], id, command);
		for (const listener of this.listeners[command] || []) {
			if (listener.id !== id) {
				newCommandListeners.push(listener);
			}
		}
		this.listeners[command] = newCommandListeners;
		this.log(this.listeners[command]);
	}

	/**
	 * For particular command notifies all subscribers
	 * @param command Calls callbacks for given command
	 * @param payload New values to provide to callbacks
	 */
	private notifySubscribers<Command extends keyof Contracts & string>(
		command: Command,
		payload?: Contracts[Command]
	) {
		this.log("notifySubscribers", this.listeners);
		const listeners = this.listeners[command];

		if (listeners) {
			for (const listener of listeners) {
				setTimeout(() => {
					// To avoid thread blocking
					try {
						listener.callback(payload);
					} catch (error) {
						console.error("One of the listeners errored", error);
					}
				}, 0);
			}
		}
	}

	/**
	 * Helper method to generate unique id
	 * @returns unique id
	 */
	private generateId(): string {
		this.latestId++;
		return this.latestId.toString();
	}

	/**
	 * Helper method to detect and clean buffer for commands without listeners
	 */
	private bufferGarbageCollection<Command extends keyof Contracts & string>(command: Command) {
		this.eventBuffer[command] = [SystemCommand.nolistener]; // Clear buffer and set nolistener to not buffer anymore for command
		delete this.toGarbageCollect[command];
		console.warn(`
			Max buffer limit reached for "${command}", no listener was detected.\n
			Make sure listener is loaded in time.\n
			Clearing event bus buffer and ignoring "${command}" from now on!
		`);
	}

	private log(...args: unknown[]) {
		if (!this.isDebugEnabled) {
			return;
		}
		console.log(...args);
	}
}
